/**
 * @file 会话与登录状态机（框架无关，React 侧用 useSyncExternalStore 订阅）
 *
 * 三条硬约束（docs/CONTRACT.md 第 3 章）：
 *   1. access token **只放内存**，避免 XSS 直接读走长效凭证；refresh token 放 localStorage
 *      （D1 无 BFF，只能这样折中，后续接 HttpOnly Cookie 时只需改本文件）；
 *   2. 401 AUTH_TOKEN_EXPIRED 自动刷新并重放一次，刷新失败立刻清会话回登录页；
 *   3. 2FA 可能以 200(stage=second_factor_required) 或 401(AUTH_SECOND_FACTOR_REQUIRED)
 *      两种形态出现，这里都归一成 'challenging' 阶段，页面只认阶段。
 */

import { ApiError, type ApiClient, type Identity, type LoginResult } from '../api';

/** 会话阶段：启动中 / 未登录 / 等待短信验证码 / 已登录。 */
export type AuthPhase = 'booting' | 'anonymous' | 'challenging' | 'authenticated';

/** 登录页与外壳共用的会话快照（不可变，整体替换以触发重渲染）。 */
export interface AuthState {
  /** 当前阶段。 */
  phase: AuthPhase;
  /** 已登录身份；未登录为 null。 */
  identity: Identity | null;
  /** 2FA 挑战号，仅 phase='challenging' 时有值。 */
  challengeId: string | null;
  /** 脱敏手机号，展示用（如 138****8000）。 */
  maskedPhone: string | null;
  /** mock 环境返回的联调验证码；生产恒为 null。 */
  devCode: string | null;
  /** 短信冷却截止时间（epoch ms），0 表示可立即发送。 */
  smsCooldownUntil: number;
  /**
   * 本次挑战的验证码发送结果。
   * 'sent' 才能对用户说「已发送」；限流/网络失败时必须显示 'failed'，
   * 否则等于把未完成的业务显示成成功（PRD 2.2「异常处理」）。
   */
  smsSendState: 'idle' | 'sent' | 'failed';
}

/** 会话能力集合，UI 只依赖这个接口，便于单测替换。 */
export interface AuthStore {
  /** 取当前快照（引用稳定，未变更时返回同一对象）。 */
  getState(): AuthState;
  /** 订阅变更，返回取消订阅函数。 */
  subscribe(listener: () => void): () => void;
  /** 取内存中的 access token。 */
  getAccessToken(): string | null;
  /** 应用启动时尝试用 refresh token 恢复会话。 */
  bootstrap(): Promise<void>;
  /** 账号密码登录；需要 2FA 时切到 challenging 并自动发一次短信。 */
  login(account: string, password: string): Promise<void>;
  /** 重发短信验证码；429 时把冷却时间写回 state 后仍然抛出，由页面提示。 */
  resendSms(): Promise<void>;
  /** 提交验证码换取令牌。 */
  verify(code: string): Promise<void>;
  /** 放弃二次验证，回到账号密码步骤（不调后端）。 */
  cancelChallenge(): void;
  /** 退出并吊销；失败也清本地会话。 */
  logout(): Promise<void>;
  /** 刷新 access token（单飞，避免并发刷新互相顶掉）。 */
  refreshSession(): Promise<boolean>;
  /** 是否具备某功能权限码。 */
  has(permission: string): boolean;
  /** 是否具备任意一个权限码。 */
  hasAny(permissions: readonly string[]): boolean;
  /** 是否属于某角色（按 role.code 判定）。 */
  hasRole(roleCode: string): boolean;
  /** 修改本人密码，成功后必须重新登录。 */
  changePassword(oldPassword: string, newPassword: string): Promise<void>;
}

/** refresh token 的 localStorage key 前缀，按端隔离，避免同一浏览器多端互踢。 */
const REFRESH_KEY_PREFIX = 'lexiloop.refresh.';

/** 短信兜底冷却秒数（后端未给出 cooldown 明细时使用，与契约 60 秒一致）。 */
const DEFAULT_SMS_COOLDOWN_SECONDS = 60;

/** 错误码：短信过于频繁。 */
const ERR_SMS_TOO_FREQUENT = 'AUTH_SMS_SEND_TOO_FREQUENT';
/** 错误码：需要二次验证。 */
const ERR_SECOND_FACTOR_REQUIRED = 'AUTH_SECOND_FACTOR_REQUIRED';

/**
 * @what 从 ApiError 里取短信冷却秒数
 * @why 后端在 429 的 details[field=cooldown].reason 里给等待秒数（见错误码表）；
 *      部分网关会把它塞进 data.cooldown，因此两处都兜一遍，最后回落 60 秒。
 * @param error 429 错误
 * @return number 冷却秒数
 */
function cooldownSecondsFrom(error: ApiError): number {
  const detail = error.details.find((item) => item.field === 'cooldown');
  const parsedDetail = detail?.reason ? Number.parseInt(detail.reason, 10) : Number.NaN;
  if (Number.isFinite(parsedDetail) && parsedDetail > 0) return parsedDetail;

  const payload = error.payload;
  if (payload && typeof payload === 'object' && 'data' in payload) {
    const data = (payload as { data?: unknown }).data;
    if (data && typeof data === 'object' && 'cooldown' in data) {
      const raw = (data as { cooldown?: unknown }).cooldown;
      if (typeof raw === 'number' && raw > 0) return raw;
    }
  }
  return DEFAULT_SMS_COOLDOWN_SECONDS;
}

/**
 * @what 从「需要二次验证」的 401 响应里取挑战信息
 * @why 契约允许 challenge_id 随错误响应返回（信封失败态不返回 data 的通用规则
 *      与 2FA 是例外，这里做兼容读取；取不到就提示重新登录，不猜测）。
 * @param error AUTH_SECOND_FACTOR_REQUIRED 错误
 * @return {{challengeId: string | null, maskedPhone: string | null}} 挑战信息
 */
function challengeFromError(error: ApiError): { challengeId: string | null; maskedPhone: string | null } {
  const payload = error.payload;
  if (payload && typeof payload === 'object' && 'data' in payload) {
    const data = (payload as { data?: unknown }).data;
    if (data && typeof data === 'object') {
      const record = data as Record<string, unknown>;
      return {
        challengeId: typeof record.challenge_id === 'string' ? record.challenge_id : null,
        maskedPhone: typeof record.masked_phone === 'string' ? record.masked_phone : null,
      };
    }
  }
  return { challengeId: null, maskedPhone: null };
}

/**
 * @kind 状态机
 * @what 会话状态与登录流程的唯一真源；React 组件只读快照 + 调方法
 * @depends ApiClient（由 runtime 注入的 getter，避免与 client 构造循环依赖）
 * @threadsafe 否：单标签页单实例；并发刷新用 refreshPromise 单飞兜住
 */
class AuthStoreImpl implements AuthStore {
  private state: AuthState = {
    phase: 'booting',
    identity: null,
    challengeId: null,
    maskedPhone: null,
    devCode: null,
    smsCooldownUntil: 0,
    smsSendState: 'idle',
  };

  private listeners = new Set<() => void>();

  /** access token 只存内存（页面刷新后靠 refresh 恢复）。 */
  private accessToken: string | null = null;

  /** 单飞刷新：并发 401 只触发一次 refresh，其余等待同一 Promise。 */
  private refreshPromise: Promise<boolean> | null = null;

  /**
   * @param getApi 延迟取 client（runtime 先建 store 再建 client，二者需要互相引用）
   * @param storageKey 端标识，用于隔离 refresh token
   */
  constructor(
    private readonly getApi: () => ApiClient,
    private readonly storageKey: string,
  ) {}

  getState(): AuthState {
    return this.state;
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  getAccessToken(): string | null {
    return this.accessToken;
  }

  /**
   * @what 用 refresh token 恢复会话
   * @return Promise<void> 无返回值
   * @side 读写 localStorage、可能调用 /auth/refresh 与 /auth/me
   */
  async bootstrap(): Promise<void> {
    if (!this.readRefreshToken()) {
      this.patch({ phase: 'anonymous' });
      return;
    }
    const refreshed = await this.refreshSession();
    if (!refreshed) {
      this.patch({ phase: 'anonymous' });
      return;
    }
    try {
      const identity = await this.getApi().getMe();
      this.patch({ phase: 'authenticated', identity });
    } catch {
      this.resetSession();
    }
  }

  /**
   * @what 账号密码登录（第一步）
   * @param account 账号或手机号
   * @param password 密码
   * @return Promise<void> 无返回值
   * @throws {ApiError} AUTH_INVALID_CREDENTIALS / AUTH_ACCOUNT_LOCKED / AUTH_ACCOUNT_DISABLED 等
   */
  async login(account: string, password: string): Promise<void> {
    try {
      const result = await this.getApi().login({ account, password });
      if (result.stage === 'second_factor_required') {
        this.enterChallenge(result.challenge_id ?? null, result.masked_phone ?? null);
        await this.safeSendSms();
        return;
      }
      await this.applyLoginResult(result);
    } catch (error) {
      // 契约允许 2FA 以 401 + AUTH_SECOND_FACTOR_REQUIRED 形态返回，两种形态都归一成挑战态
      if (error instanceof ApiError && error.code === ERR_SECOND_FACTOR_REQUIRED) {
        const { challengeId, maskedPhone } = challengeFromError(error);
        this.enterChallenge(challengeId, maskedPhone);
        await this.safeSendSms();
        return;
      }
      throw error;
    }
  }

  /**
   * @what 重发验证码
   * @return Promise<void> 无返回值
   * @throws {ApiError} AUTH_SMS_SEND_TOO_FREQUENT（同时把冷却时间写回 state）
   */
  async resendSms(): Promise<void> {
    const challengeId = this.state.challengeId;
    if (!challengeId) {
      throw new ApiError({
        code: 'AUTH_SECOND_FACTOR_EXPIRED',
        message: '挑战已过期，请重新登录',
        status: 401,
        traceId: '',
        details: [],
        payload: null,
      });
    }
    try {
      const result = await this.getApi().sendSmsCode({ challenge_id: challengeId, scene: 'login_2fa' });
      const cooldown = typeof result.cooldown === 'number' && result.cooldown > 0 ? result.cooldown : DEFAULT_SMS_COOLDOWN_SECONDS;
      this.patch({
        devCode: result.dev_code && result.dev_code.length > 0 ? result.dev_code : null,
        smsCooldownUntil: Date.now() + cooldown * 1000,
        smsSendState: 'sent',
      });
    } catch (error) {
      this.patch({ smsSendState: 'failed' });
      if (error instanceof ApiError && error.code === ERR_SMS_TOO_FREQUENT) {
        this.patch({ smsCooldownUntil: Date.now() + cooldownSecondsFrom(error) * 1000 });
      }
      throw error;
    }
  }

  /**
   * @what 提交短信验证码（第二步）
   * @param code 6 位验证码
   * @return Promise<void> 无返回值
   * @throws {ApiError} AUTH_SECOND_FACTOR_INVALID / EXPIRED / LOCKED
   */
  async verify(code: string): Promise<void> {
    const challengeId = this.state.challengeId;
    if (!challengeId) {
      throw new ApiError({
        code: 'AUTH_SECOND_FACTOR_EXPIRED',
        message: '挑战已过期，请重新登录',
        status: 401,
        traceId: '',
        details: [],
        payload: null,
      });
    }
    const result = await this.getApi().verifySecondFactor({ challenge_id: challengeId, code });
    await this.applyLoginResult(result);
  }

  /**
   * @what 放弃二次验证：本地回到账号密码步骤
   * @return void 无返回值
   * @side 清空 challenge 相关状态，不产生网络请求
   */
  cancelChallenge(): void {
    this.patch({
      phase: 'anonymous',
      challengeId: null,
      maskedPhone: null,
      devCode: null,
      smsCooldownUntil: 0,
    });
  }

  /**
   * @what 退出登录：先尽力吊销服务端令牌，再无条件清本地会话
   * @return Promise<void> 无返回值
   */
  async logout(): Promise<void> {
    try {
      await this.getApi().logout();
    } catch {
      // 退出失败不阻塞用户：本地会话必须清掉，否则会卡在「退出不了」的状态
    }
    this.resetSession();
  }

  /**
   * @what 刷新 access token（单飞）
   * @return Promise<boolean> true=已拿到新 token
   * @side 写 localStorage 的 refresh token；失败时清会话
   */
  async refreshSession(): Promise<boolean> {
    if (this.refreshPromise) return this.refreshPromise;
    const promise = this.doRefresh().finally(() => {
      this.refreshPromise = null;
    });
    this.refreshPromise = promise;
    return promise;
  }

  has(permission: string): boolean {
    return this.state.identity?.permissions?.includes(permission) ?? false;
  }

  hasAny(permissions: readonly string[]): boolean {
    return permissions.some((permission) => this.has(permission));
  }

  hasRole(roleCode: string): boolean {
    return this.state.identity?.roles?.some((role) => role.code === roleCode) ?? false;
  }

  /**
   * @what 修改本人密码
   * @param oldPassword 旧密码
   * @param newPassword 新密码
   * @return Promise<void> 无返回值
   * @throws {ApiError} AUTH_PASSWORD_POLICY_VIOLATION / AUTH_PASSWORD_REUSED 等
   * @side 成功后清会话，强制重新登录（契约要求）
   */
  async changePassword(oldPassword: string, newPassword: string): Promise<void> {
    await this.getApi().changePassword({ old_password: oldPassword, new_password: newPassword });
    this.resetSession();
  }

  /**
   * @what 用登录/验证结果落地会话
   * @param result 登录结果（含 tokens 与可选身份）
   * @return Promise<void> 无返回值
   */
  private async applyLoginResult(result: LoginResult): Promise<void> {
    if (result.tokens) this.applyTokens(result.tokens);
    const identity = result.user ?? (await this.getApi().getMe());
    this.patch({
      phase: 'authenticated',
      identity,
      challengeId: null,
      maskedPhone: null,
      devCode: null,
      smsCooldownUntil: 0,
    });
  }

  /**
   * @what 进入二次验证阶段
   * @param challengeId 挑战号
   * @param maskedPhone 脱敏手机号
   * @return void 无返回值
   */
  private enterChallenge(challengeId: string | null, maskedPhone: string | null): void {
    if (!challengeId) {
      // 拿不到挑战号就没法继续验证，直接按失效处理，避免把用户卡在白屏
      throw new ApiError({
        code: ERR_SECOND_FACTOR_REQUIRED,
        message: '二次验证挑战缺失，请重新登录',
        status: 401,
        traceId: '',
        details: [],
        payload: null,
      });
    }
    this.patch({
      phase: 'challenging',
      challengeId,
      maskedPhone,
      devCode: null,
      smsCooldownUntil: 0,
      smsSendState: 'idle',
    });
  }

  /**
   * @what 登录后自动发一次验证码；失败（如 429）不阻断进入 2FA 步骤
   * @return Promise<void> 无返回值
   */
  private async safeSendSms(): Promise<void> {
    try {
      await this.resendSms();
    } catch {
      // 冷却中/频控时页面会按 smsCooldownUntil 显示倒计时，这里不额外报错
    }
  }

  /**
   * @what 真正刷新：调 /auth/refresh，失败即清会话
   * @return Promise<boolean> 是否成功
   */
  private async doRefresh(): Promise<boolean> {
    const refreshToken = this.readRefreshToken();
    if (!refreshToken) return false;
    try {
      const tokens = await this.getApi().refreshToken({ refresh_token: refreshToken });
      this.applyTokens(tokens);
      return true;
    } catch {
      this.resetSession();
      return false;
    }
  }

  /**
   * @what 落地令牌：access 进内存，refresh 进 localStorage
   * @param tokens 令牌三元组
   * @return void 无返回值
   * @side 写 localStorage
   */
  private applyTokens(tokens: { access_token?: string; refresh_token?: string }): void {
    if (tokens.access_token) this.accessToken = tokens.access_token;
    if (tokens.refresh_token) this.writeRefreshToken(tokens.refresh_token);
  }

  /**
   * @what 清空本地会话并回到未登录
   * @return void 无返回值
   * @side 删 localStorage
   */
  private resetSession(): void {
    this.accessToken = null;
    this.removeRefreshToken();
    this.patch({
      phase: 'anonymous',
      identity: null,
      challengeId: null,
      maskedPhone: null,
      devCode: null,
      smsCooldownUntil: 0,
    });
  }

  /**
   * @what 读取 refresh token（localStorage 可能被隐私模式禁用，异常时当无）
   * @return string | null 令牌或 null
   */
  private readRefreshToken(): string | null {
    try {
      return window.localStorage.getItem(REFRESH_KEY_PREFIX + this.storageKey);
    } catch {
      return null;
    }
  }

  /**
   * @what 写入 refresh token
   * @param value 令牌
   * @return void 无返回值
   */
  private writeRefreshToken(value: string): void {
    try {
      window.localStorage.setItem(REFRESH_KEY_PREFIX + this.storageKey, value);
    } catch {
      // 存不进去只影响刷新能力，不影响本次会话
    }
  }

  /**
   * @what 删除 refresh token
   * @return void 无返回值
   */
  private removeRefreshToken(): void {
    try {
      window.localStorage.removeItem(REFRESH_KEY_PREFIX + this.storageKey);
    } catch {
      // 同上，忽略
    }
  }

  /**
   * @what 合并状态并通知订阅者
   * @param patch 局部更新
   * @return void 无返回值
   */
  private patch(patch: Partial<AuthState>): void {
    this.state = { ...this.state, ...patch };
    for (const listener of this.listeners) listener();
  }
}

/** 兼容 401 挑战形态的导出，供页面判断错误码用。 */
export { challengeFromError };

/**
 * @what 创建会话状态机
 * @param getApi 延迟取 api-client 的 getter
 * @param storageKey 端标识（teacher/admin/crm），用于隔离 refresh token
 * @return AuthStore 会话能力集合
 */
export function createAuthStore(getApi: () => ApiClient, storageKey: string): AuthStore {
  return new AuthStoreImpl(getApi, storageKey);
}
